// OP-3605 — Transferências (ESL1 para ver): movimento de patrimônio entre
// praças. Não é pedido: aqui ninguém está atendendo produtor, a base é que se
// reequilibra. A lista não traz itens — eles só existem no detalhe.
import { FC, useEffect, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useEstoqueTransferencias } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import { StatusTransferencia, TransferenciaRow, TransferenciasFilters } from '../../../../types/estoque'
import {
  BarraChips, BD, Button, CaixaLista, Card, Chip, Field, FiltersRow, ListaCartoes, PageSubtitle,
  PageTitle, Select, SoDesktop, Spinner, StatusBadge, Table, TabelaWrap,
} from '../../styles'
import { fmtDate, fmtDateTime } from '../../statusUi'
import { Cartao } from '../Cartao'
import { FabLink } from '../FabLink'
import { statusDaUrl } from '../filtrosUrl'
import { STATUS_TRANSFERENCIA_UI } from '../Transferencia/regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const STATUS: StatusTransferencia[] = ['SOLICITADA', 'APROVADA', 'EM_SEPARACAO', 'EM_TRANSITO', 'RECEBIDA', 'CONFERIDA', 'CANCELADA']
const COLS = 6

/** O que cada estado espera — o mesmo verbo do trilho da ficha. */
const ACAO_DO_STATUS: Partial<Record<StatusTransferencia, string>> = {
  SOLICITADA: 'aprovar',
  APROVADA: 'iniciar separação',
  EM_SEPARACAO: 'despachar',
  EM_TRANSITO: 'confirmar recebimento',
  RECEBIDA: 'conferir item a item',
}

export const EstoqueTransferencias: FC = () => {
  const navigate = useNavigate()
  const { pode } = usePerfilEstoque()
  const podeTransferir = pode('TRANSFERIR')
  const [searchParams] = useSearchParams()
  const statusUrl = statusDaUrl(searchParams, STATUS)
  const [draft, setDraft] = useState<TransferenciasFilters>(statusUrl ? { status: statusUrl, page: 1 } : { page: 1 })
  const [applied, setApplied] = useState<TransferenciasFilters>(statusUrl ? { status: statusUrl, page: 1 } : { page: 1 })
  const { rows, total, lastPage, isLoading, isError, isUnauthorized, mutate } = useEstoqueTransferencias(applied)
  const pagina = Math.max(1, Number(applied.page) || 1)

  useEffect(() => {
    if (!statusUrl) return
    setDraft((d) => ({ ...d, status: statusUrl, page: 1 }))
    setApplied((a) => ({ ...a, status: statusUrl, page: 1 }))
  }, [statusUrl])

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const escolherStatus = (status: StatusTransferencia | '') => {
    setDraft((d) => ({ ...d, status, page: 1 }))
    setApplied((a) => ({ ...a, status, page: 1 }))
  }

  const cartao = (t: TransferenciaRow) => {
    const ui = STATUS_TRANSFERENCIA_UI[t.status] ?? { bg: '#F3F4F6', color: '#374151', label: t.status }
    const encerrada = t.status === 'CONFERIDA' || t.status === 'CANCELADA'
    const acao = ACAO_DO_STATUS[t.status]
    return (
      <Cartao
        key={t.id}
        to={`/interno/estoque/transferencias/${t.id}`}
        codigo={t.numero}
        badge={ui}
        titulo={`${t.origemNome} → ${t.destinoNome}`}
        esmaecido={encerrada}
        meta={(
          <>
            {t.transportadora ? `${t.transportadora} · ` : ''}{t.codigoRastreio ? `rastreio ${t.codigoRastreio} · ` : ''}aberta {fmtDate(t.criadoEm)}
            {t.motivo ? <div>{t.motivo}</div> : null}
          </>
        )}
        rodape={acao
          ? <span style={{ color: BD.grayText }}>Próxima ação: {acao}</span>
          : <span style={{ color: BD.grayText }}>{t.status === 'CONFERIDA' ? 'material no estoque do destino' : 'encerrada'}</span>}
        rodapeFim={t.previsaoChegada ? `chega ${fmtDate(t.previsaoChegada)}` : undefined}
      />
    )
  }

  const linha = (t: TransferenciaRow) => {
    const ui = STATUS_TRANSFERENCIA_UI[t.status] ?? { bg: '#F3F4F6', color: '#374151', label: t.status }
    return (
      <tr key={t.id} onClick={() => navigate(`/interno/estoque/transferencias/${t.id}`)} style={{ cursor: 'pointer' }}>
        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{t.numero}</td>
        <td><StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge></td>
        <td><b>{t.origemNome}</b> → <b>{t.destinoNome}</b>{(t.transportadora || t.codigoRastreio) && <div style={{ fontSize: 12, color: BD.grayText }}>{[t.transportadora, t.codigoRastreio ? `rastreio ${t.codigoRastreio}` : null].filter(Boolean).join(' · ')}</div>}</td>
        <td style={{ fontSize: 13 }}>{fmtDate(t.previsaoChegada)}</td>
        <td style={{ fontSize: 13 }}>{fmtDateTime(t.despachadoEm)}</td>
        <td style={{ fontSize: 13 }}>{fmtDateTime(t.criadoEm)}{t.motivo && <div style={{ fontSize: 11, color: BD.grayText }}>{t.motivo}</div>}</td>
      </tr>
    )
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>Transferências</PageTitle>
          <PageSubtitle>
            Patrimônio mudando de praça. O material só fica disponível no destino depois da conferência: receber diz que a caixa chegou, conferir diz o que havia dentro.
          </PageSubtitle>
        </div>
        {podeTransferir && (
          <SoDesktop>
            <Button onClick={() => navigate('/interno/estoque/transferencias/nova')}>Nova transferência</Button>
          </SoDesktop>
        )}
      </div>

      <BarraChips role="group" aria-label="Status da transferência">
        <Chip type="button" $ativo={!applied.status} onClick={() => escolherStatus('')}>Todas</Chip>
        {STATUS.map((s) => (
          <Chip key={s} type="button" $ativo={applied.status === s} onClick={() => escolherStatus(s)}>
            {STATUS_TRANSFERENCIA_UI[s].label}
          </Chip>
        ))}
      </BarraChips>

      <SoDesktop>
        <FiltersRow>
          <Field>
            <label>Status</label>
            <Select value={draft.status ?? ''} onChange={(e) => setDraft({ ...draft, status: e.target.value as StatusTransferencia | '' })}>
              <option value="">todos</option>
              {STATUS.map((s) => <option key={s} value={s}>{STATUS_TRANSFERENCIA_UI[s].label}</option>)}
            </Select>
          </Field>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}><Button $variant="secondary" onClick={() => setApplied({ ...draft, page: 1 })}>Aplicar</Button></div>
        </FiltersRow>
      </SoDesktop>

      <CaixaLista>
        <div style={{ padding: '8px 12px', fontSize: 13, color: BD.grayText }}>{isLoading ? 'Carregando…' : `${total} transferência${total === 1 ? '' : 's'}`}</div>
        <TabelaWrap>
          <Table>
            <thead><tr><th>Número</th><th>Status</th><th>De → para</th><th>Previsão</th><th>Despachada</th><th>Aberta em</th></tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24 }}><Spinner /></td></tr>}
              {!isLoading && isError && (
                <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.red }}>
                  Não foi possível carregar as transferências. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
                </td></tr>
              )}
              {!isLoading && !isError && rows.length === 0 && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>Nenhuma transferência com os filtros atuais.</td></tr>}
              {!isLoading && !isError && rows.map(linha)}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {isLoading && <Card style={{ textAlign: 'center', padding: 24 }}><Spinner /></Card>}
          {!isLoading && isError && (
            <Card style={{ padding: 20, color: BD.red }}>
              Não foi possível carregar as transferências. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
            </Card>
          )}
          {!isLoading && !isError && rows.length === 0 && <Card style={{ padding: 20, color: BD.grayText }}>Nenhuma transferência com os filtros atuais.</Card>}
          {!isLoading && !isError && rows.map(cartao)}
        </ListaCartoes>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: 12 }}>
          <Button $variant="secondary" disabled={pagina <= 1} onClick={() => setApplied((a) => ({ ...a, page: pagina - 1 }))}>‹ Anterior</Button>
          <span style={{ fontSize: 13, color: BD.grayText }}>Página {pagina}</span>
          <Button $variant="secondary" disabled={!!isError || isLoading || !!lastPage} onClick={() => setApplied((a) => ({ ...a, page: pagina + 1 }))}>Próxima ›</Button>
        </div>
      </CaixaLista>

      {podeTransferir && (
        <FabLink to="/interno/estoque/transferencias/nova" rotulo="Nova transferência" />
      )}
    </>
  )
}

export default EstoqueTransferencias
